import {
  Accommodation,
  AccommodationOccupancy,
  CategoryLimitsInput,
  MaxOccupancyByType,
  Multimedia,
  MultimediaTranslation,
  Translation,
} from "../../../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_pb";
import { AccommodationEntity } from "../../domain/Accommodation.entity";
import { isEmpty } from "lodash";
import { CategoryLimits } from "../../../extrasCategories/domain/entities/CategoryLimits";
import { CategoryLimitsMapper } from "../../../extrasCategories/infrastructure/mappers/CategoryLimitsMapper";
import { mapCategoryLimitsFormDataToDomain } from "../../../extrasCategories/presentation/mappers/category-limits.mapper";
import { CategoryLimitsFormData } from "../../../extrasCategories/presentation/types/CategoryLimitsTypes";

interface AsGrpcOptions {
  includeMultimedia?: boolean;
}

export function asGrpc(
  entity: AccommodationEntity,
  { includeMultimedia = true }: AsGrpcOptions = {}
): Accommodation {
  const accommodation = new Accommodation();
  accommodation.setId(entity.id);
  accommodation.setTicker(entity.ticker);
  accommodation.setType(entity.type);
  accommodation.setActive(entity.active);
  accommodation.setOccupancyCombinationsList(
    entity.customOccupancy?.occupancyCombinationsList || []
  );
  accommodation.setAccommodationBaseAdults(entity.accommodationBaseAdults);

  accommodation.setTranslationsList(
    entity.translationsList.map(it => {
      const translation = new Translation();
      translation.setName(it.name);
      translation.setLocale(it.locale);
      translation.setDescription(it.description);
      return translation;
    })
  );

  if (includeMultimedia) {
    accommodation.setMultimediaList(
      entity.multimediaList
        .filter(it => !it.url.includes("blob:"))
        .map(it => {
          const multimedia = new Multimedia();
          multimedia.setId(it.id);
          multimedia.setUrl(it.url);
          multimedia.setFilename(it.filename);

          const translations = it.translations || {};
          multimedia.setTranslationsList(
            Object.keys(translations).map(locale => {
              const translation = new MultimediaTranslation();
              translation.setLocale(locale);
              translation.setTitle(translations[locale].title);
              translation.setDescription(translations[locale].description || "");
              return translation;
            })
          );

          return multimedia;
        })
    );
  }

  if (entity.customOccupancy && !isEmpty(entity.customOccupancy.maxOccupancyByType)) {
    const occupancy = new AccommodationOccupancy();
    occupancy.setMaxOccupancy(entity.customOccupancy.maxOccupancy);

    const maxOccupancyByType = new MaxOccupancyByType();
    maxOccupancyByType.setSenior(entity.customOccupancy.maxOccupancyByType?.senior || 0);
    maxOccupancyByType.setAdults(entity.customOccupancy.maxOccupancyByType?.adults || 0);
    maxOccupancyByType.setJuniors(entity.customOccupancy.maxOccupancyByType?.juniors || 0);
    maxOccupancyByType.setChildren(entity.customOccupancy.maxOccupancyByType?.children || 0);
    maxOccupancyByType.setBabies(entity.customOccupancy.maxOccupancyByType?.babies || 0);

    occupancy.setMaxOccupancyByType(maxOccupancyByType);
    accommodation.setOccupancy(occupancy);
  }

  return accommodation;
}

/**
 * The `category_limits` of a create or update request, with every block shown, on or off, and
 * only while the section is visible. Blocks the form has not touched keep the loaded state.
 * `Accommodation` itself never carries the section (`asGrpc` does not read `categoryLimits`).
 */
export function asCategoryLimitsGrpc(
  limits: CategoryLimits | undefined,
  formData: CategoryLimitsFormData | undefined
): CategoryLimitsInput | undefined {
  if (!limits?.isSectionVisible()) {
    return undefined;
  }
  return CategoryLimitsMapper.toProto(mapCategoryLimitsFormDataToDomain(limits, formData));
}
